import './App.css';
import { BrowserRouter as Router, Routes, Route, useLocation, useSearchParams } from 'react-router-dom';
import { ClubProvider, useClub } from './contexts/ClubContext';
import Home from './pages/Home';
import Register from './pages/Register';
import Teams from './pages/Teams';
import Schedule from './pages/Schedule';
import KitShop from './pages/KitShop';
import Checkout from './pages/Checkout';
import Contact from './pages/Contact';
import Login from './pages/Login';
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import ManageCoaches from './pages/admin/ManageCoaches';
import ManagePlayers from './pages/admin/ManagePlayers';
import ManageTeams from './pages/admin/ManageTeams';
import ManageSchedule from './pages/admin/ManageSchedule';
import ManageShop from './pages/admin/ManageShop';
import ManageRegistrations from './pages/admin/ManageRegistrations';
import ManageOrders from './pages/admin/ManageOrders';
import ManageContent from './pages/admin/ManageContent';
import ManageSubscription from './pages/admin/ManageSubscription';
import TakeAttendance from './pages/admin/TakeAttendance';
import ManageGallery from './pages/admin/ManageGallery';
import Gallery from './pages/Gallery';
import PlayerDashboard from './pages/PlayerDashboard';
import CoachDashboard from './pages/CoachDashboard';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import AdminManagement from './pages/admin/AdminManagement';
import Landing from './pages/Landing';
import CreateClub from './pages/CreateClub';
import AdminRoute from './components/admin/AdminRoute';
import SuperAdminRoute from './components/admin/SuperAdminRoute';
import CoachRoute from './components/CoachRoute';
import SubscriptionGuard from './components/SubscriptionGuard';
import Navbar from './components/Navbar';

// Loading screen while club is being detected
function ClubLoading() {
  return (
    <div className="club-loading">
      <div className="loading-spinner"></div>
      <p>Loading...</p>
    </div>
  );
}

// Error screen when club not found
function ClubError({ error }) {
  return (
    <div className="club-error">
      <h1>Club Not Found</h1>
      <p>{error}</p>
      <p>Please check the URL and try again.</p>
      <div style={{ marginTop: '20px' }}>
        <a 
          href="/" 
          style={{ 
            color: '#ce1126', 
            textDecoration: 'none',
            padding: '10px 20px',
            border: '1px solid #ce1126',
            borderRadius: '6px'
          }}
        >
          Go to ClubLink TT Home
        </a>
      </div>
    </div>
  );
}

// Layout component that conditionally shows navbar/footer
function Layout({ children }) {
  const location = useLocation();
  const { club, loading, error } = useClub();
  const isAdminPage = location.pathname.startsWith('/admin');
  const isSuperAdminPage = location.pathname.startsWith('/super-admin');

  // Super admin pages don't need club context
  if (isSuperAdminPage) {
    return <div className="app">{children}</div>;
  }

  if (loading) {
    return <ClubLoading />;
  }

  if (error) {
    return <ClubError error={error} />;
  }

  // Admin pages get subscription guard
  if (isAdminPage) {
    return (
      <div className="app">
        <SubscriptionGuard>
          {children}
        </SubscriptionGuard>
      </div>
    );
  }

  // Public pages also get subscription guard
  return (
    <div className="app">
      <SubscriptionGuard>
        <Navbar />
        {children}
        {/* Footer */}
        <footer className="footer">
          <div className="footer-logo">
            <span className="footer-icon">⚽</span>
            <span>CLUBLINK TT</span>
          </div>
          <p>© 2026 ClubLink TT. All rights reserved.</p>
          <p className="footer-location">🇹🇹 Trinidad & Tobago</p>
        </footer>
      </SubscriptionGuard>
    </div>
  );
}

// Wrapper component to handle setup and landing pages outside ClubProvider
function AppRoutes() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const clubParam = searchParams.get('club');
  
  // Create Club page doesn't need ClubProvider (for new club registration with payment)
  if (location.pathname === '/create-club') {
    return <CreateClub />;
  }
  
  // Landing page shown when no club parameter is specified (root URL only)
  const needsClubParam = !clubParam && 
    location.pathname === '/' && 
    !location.pathname.startsWith('/super-admin');
  
  if (needsClubParam) {
    return <Landing />;
  }
  
  return (
    <ClubProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/register" element={<Register />} />
          <Route path="/teams" element={<Teams />} />
          <Route path="/schedule" element={<Schedule />} />
          <Route path="/kitshop" element={<KitShop />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/gallery" element={<Gallery />} />
          <Route path="/dashboard" element={<PlayerDashboard />} />
          <Route path="/coach" element={
            <CoachRoute>
              <CoachDashboard />
            </CoachRoute>
          } />
          <Route path="/login" element={<Login />} />
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          } />
          <Route path="/admin/coaches" element={
            <AdminRoute>
              <ManageCoaches />
            </AdminRoute>
          } />
          <Route path="/admin/players" element={
            <AdminRoute>
              <ManagePlayers />
            </AdminRoute>
          } />
          <Route path="/admin/teams" element={
            <AdminRoute>
              <ManageTeams />
            </AdminRoute>
          } />
          <Route path="/admin/schedule" element={
            <AdminRoute>
              <ManageSchedule />
            </AdminRoute>
          } />
          <Route path="/admin/shop" element={
            <AdminRoute>
              <ManageShop />
            </AdminRoute>
          } />
          <Route path="/admin/registrations" element={
            <AdminRoute>
              <ManageRegistrations />
            </AdminRoute>
          } />
          <Route path="/admin/orders" element={
            <AdminRoute>
              <ManageOrders />
            </AdminRoute>
          } />
          <Route path="/admin/content" element={
            <AdminRoute>
              <ManageContent />
            </AdminRoute>
          } />
          <Route path="/admin/attendance/:eventId" element={
            <AdminRoute>
              <TakeAttendance />
            </AdminRoute>
          } />
          <Route path="/admin/gallery" element={
            <AdminRoute>
              <ManageGallery />
            </AdminRoute>
          } />
          <Route path="/admin/subscription" element={
            <AdminRoute>
              <ManageSubscription />
            </AdminRoute>
          } />
          {/* Super Admin Routes */}
          <Route path="/super-admin" element={
            <SuperAdminRoute>
              <SuperAdminDashboard />
            </SuperAdminRoute>
          } />
          <Route path="/super-admin/clubs" element={
            <SuperAdminRoute>
              <SuperAdminDashboard />
            </SuperAdminRoute>
          } />
          <Route path="/super-admin/admins" element={
            <SuperAdminRoute>
              <AdminManagement />
            </SuperAdminRoute>
          } />
        </Routes>
      </Layout>
    </ClubProvider>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
